/**
 * PaymentHistoryPage - Transaction History for Q.AI Token Purchases
 * Displays the authenticated user's payment history with pagination
 *
 * Route: /q-ai/payments/history
 *
 * Features:
 * - Lists user's past token purchases (JWT-authenticated)
 * - Shows payment status, amount, tokens, and date
 * - Pagination (10 items per page)
 * - Empty state for new users
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Button,
    CircularProgress,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Pagination,
    Alert,
    IconButton,
    Tooltip
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import AddIcon from '@mui/icons-material/Add';
import QAIHeader from '../../Components/QAI/QAIHeader';
import PaymentStatusBadge from '../../Components/QAI/PaymentStatusBadge';
import { getPaymentHistory } from '../../api/paymentApi';
import { SUPPORT_EMAIL } from '../../config';

// ═══════════════════════════════════════════════════════════════════════════════
// BRAND CONSTANTS (Matching existing Q.AI pages)
// ═══════════════════════════════════════════════════════════════════════════════

const BRAND = {
    primary: '#27B56D',
    primaryLight: '#22C55E',
    background: '#0A0A0F',
    surface: '#12121A',
    surfaceHover: '#1A1A24',
    text: '#FFFFFF',
    textSecondary: '#E2E8F0',
    textMuted: '#94A3B8',
    border: 'rgba(255, 255, 255, 0.08)',
    error: '#EF4444'
};

const ITEMS_PER_PAGE = 10;

// ═══════════════════════════════════════════════════════════════════════════════
// HELPER FUNCTIONS
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Format date for display
 */
const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    });
};

/**
 * Format currency for display
 */
const formatCurrency = (amount, currency = 'usd') => {
    if (amount === null || amount === undefined) return '-';
    return `$${Number(amount).toFixed(2)} ${currency.toUpperCase()}`;
};

// ═══════════════════════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════

const PaymentHistoryPage = () => {
    const navigate = useNavigate();

    // State
    const [payments, setPayments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);

    // ═══════════════════════════════════════════════════════════════════════════
    // FETCH PAYMENT HISTORY
    // ═══════════════════════════════════════════════════════════════════════════

    const fetchHistory = useCallback(async (pageNum = 1) => {
        setLoading(true);
        setError(null);

        try {
            const result = await getPaymentHistory(pageNum, ITEMS_PER_PAGE);

            if (!result.success) {
                setError(result.error || 'Failed to load payment history');
                return;
            }

            const data = result.data;
            setPayments(data.payments || []);
            setTotalPages(Math.ceil((data.total || 0) / ITEMS_PER_PAGE));

            console.log('[PaymentHistoryPage] Loaded:', {
                payments: data.payments?.length,
                page: pageNum
            });
        } catch (err) {
            console.error('[PaymentHistoryPage] Error:', err);
            setError('Failed to load payment history. Please try again.');
        } finally {
            setLoading(false);
        }
    }, []);

    // Initial load
    useEffect(() => {
        fetchHistory(1);
    }, [fetchHistory]);

    // ═══════════════════════════════════════════════════════════════════════════
    // HANDLERS
    // ═══════════════════════════════════════════════════════════════════════════

    const handlePageChange = (event, newPage) => {
        setPage(newPage);
        fetchHistory(newPage);
    };

    const handleRefresh = () => {
        fetchHistory(page);
    };

    const handleBuyTokens = () => {
        navigate('/q-ai/coach');
    };

    // ═══════════════════════════════════════════════════════════════════════════
    // RENDER - EMPTY STATE
    // ═══════════════════════════════════════════════════════════════════════════

    const renderEmptyState = () => (
        <Box
            sx={{
                textAlign: 'center',
                py: 8
            }}
        >
            <ReceiptLongIcon
                sx={{
                    fontSize: 80,
                    color: BRAND.textMuted,
                    mb: 2,
                    opacity: 0.5
                }}
            />
            <Typography
                variant="h6"
                sx={{
                    color: BRAND.textSecondary,
                    mb: 1
                }}
            >
                No Transactions Yet
            </Typography>
            <Typography
                sx={{
                    color: BRAND.textMuted,
                    mb: 4,
                    maxWidth: 400,
                    mx: 'auto'
                }}
            >
                You haven't made any token purchases yet. Buy tokens to start using Q.AI features.
            </Typography>
            <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleBuyTokens}
                sx={{
                    backgroundColor: BRAND.primary,
                    color: '#000',
                    fontWeight: 600,
                    px: 4,
                    py: 1.5,
                    '&:hover': {
                        backgroundColor: BRAND.primaryLight
                    }
                }}
            >
                Buy Tokens
            </Button>
        </Box>
    );

    // ═══════════════════════════════════════════════════════════════════════════
    // RENDER - TABLE
    // ═══════════════════════════════════════════════════════════════════════════

    const renderTable = () => (
        <>
            <TableContainer>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ color: BRAND.textMuted, borderColor: BRAND.border }}>
                                ID
                            </TableCell>
                            <TableCell sx={{ color: BRAND.textMuted, borderColor: BRAND.border }}>
                                Date
                            </TableCell>
                            <TableCell sx={{ color: BRAND.textMuted, borderColor: BRAND.border }}>
                                Amount
                            </TableCell>
                            <TableCell sx={{ color: BRAND.textMuted, borderColor: BRAND.border }}>
                                Tokens
                            </TableCell>
                            <TableCell sx={{ color: BRAND.textMuted, borderColor: BRAND.border }}>
                                Status
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {payments.map((payment) => (
                            <TableRow
                                key={payment.id}
                                sx={{
                                    '&:hover': {
                                        backgroundColor: BRAND.surfaceHover
                                    }
                                }}
                            >
                                <TableCell
                                    sx={{
                                        color: BRAND.textSecondary,
                                        borderColor: BRAND.border,
                                        fontFamily: 'monospace'
                                    }}
                                >
                                    #{payment.id}
                                </TableCell>
                                <TableCell sx={{ color: BRAND.textSecondary, borderColor: BRAND.border }}>
                                    {formatDate(payment.created_at)}
                                </TableCell>
                                <TableCell sx={{ color: BRAND.text, borderColor: BRAND.border, fontWeight: 500 }}>
                                    {formatCurrency(payment.price_amount, payment.price_currency)}
                                </TableCell>
                                <TableCell
                                    sx={{
                                        color: payment.tokens_credited ? BRAND.primary : BRAND.textMuted,
                                        borderColor: BRAND.border,
                                        fontWeight: payment.tokens_credited ? 600 : 400
                                    }}
                                >
                                    {payment.tokens_credited ? '+' : ''}{payment.tokens_to_credit || '-'}
                                </TableCell>
                                <TableCell sx={{ borderColor: BRAND.border }}>
                                    <PaymentStatusBadge status={payment.status} size="small" />
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Pagination */}
            {totalPages > 1 && (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        mt: 3,
                        pt: 2,
                        borderTop: `1px solid ${BRAND.border}`
                    }}
                >
                    <Pagination
                        count={totalPages}
                        page={page}
                        onChange={handlePageChange}
                        sx={{
                            '& .MuiPaginationItem-root': {
                                color: BRAND.textSecondary,
                                borderColor: BRAND.border,
                                '&:hover': {
                                    backgroundColor: BRAND.surfaceHover
                                },
                                '&.Mui-selected': {
                                    backgroundColor: BRAND.primary,
                                    color: '#000',
                                    '&:hover': {
                                        backgroundColor: BRAND.primaryLight
                                    }
                                }
                            }
                        }}
                    />
                </Box>
            )}
        </>
    );

    // ═══════════════════════════════════════════════════════════════════════════
    // RENDER
    // ═══════════════════════════════════════════════════════════════════════════

    return (
        <Box
            sx={{
                minHeight: '100vh',
                background: BRAND.background,
                display: 'flex',
                flexDirection: 'column'
            }}
        >
            {/* Unified Q.AI Header */}
            <QAIHeader
                showTokens={true}
                showHelp={false}
                showSettings={false}
                showHomeButton={true}
            />

            {/* Page Content */}
            <Box sx={{ flex: 1, py: 4, px: 3 }}>
                {/* Page Title with Refresh */}
                <Box
                    sx={{
                        maxWidth: 900,
                        mx: 'auto',
                        mb: 3,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                    }}
                >
                    <Box>
                        <Typography
                            variant="h4"
                            sx={{
                                color: BRAND.text,
                                fontWeight: 600
                            }}
                        >
                            Transaction History
                        </Typography>
                    </Box>
                    <Tooltip title="Refresh">
                        <IconButton
                            onClick={handleRefresh}
                            disabled={loading}
                            sx={{
                                color: BRAND.textMuted,
                                '&:hover': {
                                    color: BRAND.text,
                                    backgroundColor: 'rgba(255, 255, 255, 0.05)'
                                }
                            }}
                        >
                            <RefreshIcon />
                        </IconButton>
                    </Tooltip>
                </Box>

            {/* Main Content */}
            <Paper
                elevation={0}
                sx={{
                    background: BRAND.surface,
                    border: `1px solid ${BRAND.border}`,
                    borderRadius: 2,
                    maxWidth: 900,
                    mx: 'auto',
                    overflow: 'hidden'
                }}
            >
                {/* Loading State */}
                {loading && (
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            py: 8
                        }}
                    >
                        <CircularProgress sx={{ color: BRAND.primary }} />
                    </Box>
                )}

                {/* Error State */}
                {!loading && error && (
                    <Box sx={{ p: 3 }}>
                        <Alert
                            severity="error"
                            action={
                                <Button
                                    color="inherit"
                                    size="small"
                                    onClick={handleRefresh}
                                >
                                    Retry
                                </Button>
                            }
                            sx={{
                                backgroundColor: 'rgba(239, 68, 68, 0.1)',
                                color: '#FCA5A5',
                                border: '1px solid rgba(239, 68, 68, 0.2)'
                            }}
                        >
                            {error}
                        </Alert>
                    </Box>
                )}

                {/* Empty State */}
                {!loading && !error && payments.length === 0 && renderEmptyState()}

                {/* Table */}
                {!loading && !error && payments.length > 0 && renderTable()}
            </Paper>

            {/* Footer Link */}
            {!loading && payments.length > 0 && (
                <Box
                    sx={{
                        textAlign: 'center',
                        mt: 4
                    }}
                >
                    <Button
                        variant="outlined"
                        startIcon={<AddIcon />}
                        onClick={handleBuyTokens}
                        sx={{
                            borderColor: BRAND.border,
                            color: BRAND.textSecondary,
                            '&:hover': {
                                borderColor: BRAND.primary,
                                backgroundColor: 'rgba(39, 181, 109, 0.05)'
                            }
                        }}
                    >
                        Buy More Tokens
                    </Button>
                </Box>
            )}

            {/* Help text */}
            <Typography
                sx={{
                    color: BRAND.textMuted,
                    fontSize: '0.8rem',
                    mt: 4,
                    textAlign: 'center'
                }}
            >
                Having issues with a payment? Contact {SUPPORT_EMAIL}
            </Typography>
            </Box>
        </Box>
    );
};

export default PaymentHistoryPage;
